import { useMutation, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/rc/keys";
import { usePosIdentityStore } from "@/stores/posIdentityStore";

/** True when at least one non-admin staff role in the restaurant has a POS PIN set (PIN gate applies). */
export const useStaffPinsConfigured = (
  restaurantId: string | null | undefined
) => {
  return useQuery({
    queryKey: restaurantId
      ? queryKeys.staff.staffPinsConfigured(restaurantId)
      : ["staff-pins-configured", "__none__"],
    enabled: Boolean(restaurantId),
    staleTime: 30 * 1000,
    queryFn: async (): Promise<boolean> => {
      if (!restaurantId) return false;
      const { data, error } = await supabase.rpc("restaurant_has_staff_pins", {
        p_restaurant_id: restaurantId,
      });
      if (error) throw error;
      return Boolean(data);
    },
  });
};

/** Verifies a staff PIN against the backend and sets POS identity on success. */
export function useVerifyStaffPin(restaurantId: string | null | undefined) {
  const { t } = useTranslation("common");
  const setIdentity = usePosIdentityStore((s) => s.setIdentity);

  const mutation = useMutation({
    mutationFn: async (pin: string) => {
      if (!restaurantId) throw new Error("No restaurant");

      const { data, error } = await supabase.rpc("verify_staff_pin", {
        p_restaurant_id: restaurantId,
        p_pin: pin,
      });

      if (error) throw error;

      const row = Array.isArray(data) ? data[0] : null;
      if (!row || typeof row !== "object" || !("user_id" in row)) {
        throw new Error("INVALID_PIN");
      }

      return {
        userId: row.user_id as string,
        fullName: row.full_name as string | null,
      };
    },
    onSuccess: ({ userId, fullName }) => {
      setIdentity(userId, fullName);
    },
    onError: (error) => {
      console.error("Error:", error);
    },
  });

  const error = mutation.isError
    ? mutation.error?.message === "INVALID_PIN"
      ? t("auth.pinInvalid")
      : t("auth.pinError")
    : null;

  return {
    verify: mutation.mutate,
    verifying: mutation.isPending,
    error,
    reset: mutation.reset,
  };
}
